import React, { useState, useRef, useEffect } from 'react';
import './Popover.css';

const Popover = ({ 
  children, 
  content, 
  trigger = 'hover', 
  placement = 'top',
  className = '',
  disabled = false,
  offset = 8,
  compact = false,
  ...props 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const showPopover = () => {
    if (disabled) return;
    setIsVisible(true);
  };

  const hidePopover = () => {
    if (disabled) return;
    setIsVisible(false);
  };

  const togglePopover = () => {
    if (disabled) return;
    setIsVisible(!isVisible);
  };

  const updatePosition = () => {
    if (!triggerRef.current || !popoverRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const popoverRect = popoverRef.current.getBoundingClientRect();
    
    let top, left;

    switch (placement) {
      case 'top':
        top = triggerRect.top - popoverRect.height - offset;
        left = triggerRect.left + (triggerRect.width / 2) - (popoverRect.width / 2);
        break;
      case 'bottom':
        top = triggerRect.bottom + offset;
        left = triggerRect.left + (triggerRect.width / 2) - (popoverRect.width / 2);
        break;
      case 'left':
        top = triggerRect.top + (triggerRect.height / 2) - (popoverRect.height / 2);
        left = triggerRect.left - popoverRect.width - offset;
        break;
      case 'right':
        top = triggerRect.top + (triggerRect.height / 2) - (popoverRect.height / 2);
        left = triggerRect.right + offset;
        break;
      default:
        top = triggerRect.top - popoverRect.height - offset;
        left = triggerRect.left + (triggerRect.width / 2) - (popoverRect.width / 2);
    }

    // Ensure popover stays within viewport
    if (top < 0) top = triggerRect.bottom + offset;
    if (left < 0) left = 8;
    if (left + popoverRect.width > window.innerWidth) left = window.innerWidth - popoverRect.width - 8;

    setPosition({ top, left });
  };

  useEffect(() => {
    if (isVisible) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition);
      
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition);
      };
    }
  }, [isVisible, placement, offset]);

  const handleMouseEnter = () => {
    if (trigger === 'hover') showPopover();
  };

  const handleMouseLeave = () => {
    if (trigger === 'hover') hidePopover();
  };

  const handleClick = () => {
    if (trigger === 'click') togglePopover();
  };

  const handleFocus = () => {
    if (trigger === 'focus') showPopover();
  };

  const handleBlur = () => {
    if (trigger === 'focus') hidePopover();
  };

  const triggerProps = {
    ref: triggerRef,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onClick: handleClick,
    onFocus: handleFocus,
    onBlur: handleBlur,
    ...props
  };

  return (
    <div className="popover-container">
      <div {...triggerProps}>
        {children}
      </div>
      
      {isVisible && (
        <div
          ref={popoverRef}
          className={`popover popover-${placement} ${className} ${compact ? 'popover-compact' : ''}`}
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: 1000,
            ...(compact && {
              minWidth: 'auto',
              maxWidth: 'auto',
              width: 'fit-content'
            })
          }}
          onMouseEnter={trigger === 'hover' ? showPopover : undefined}
          onMouseLeave={trigger === 'hover' ? hidePopover : undefined}
        >
          <div className="popover-content">
            {content}
          </div>
          <div className={`popover-arrow popover-arrow-${placement}`}></div>
        </div>
      )}
    </div>
  );
};

export default Popover;
